import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { LandingPage } from './components/LandingPage';
import { LoginPage } from './components/LoginPage';
import { DashboardLayout } from './components/DashboardLayout';
import { Navigation } from './components/Navigation';
import type { Document } from './components/DocumentManagement';
import { User, Equipment, BorrowRequest } from './types';
import { toast, Toaster } from 'sonner';
import * as api from './utils/supabase/api';
import { signOut, getSession, supabase, isPendingCoachAuthUser } from './utils/supabase/auth';
import { validateHumanName } from './utils/nameValidator';

// Lazy-loaded components - only loaded when the user navigates to them
const AdminDashboard = lazy(() => import('./components/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const StudentDataBank = lazy(() => import('./components/StudentDataBank').then(m => ({ default: m.StudentDataBank })));
const UserDashboard = lazy(() => import('./components/UserDashboard').then(m => ({ default: m.UserDashboard })));
const EquipmentManagement = lazy(() => import('./components/EquipmentManagement').then(m => ({ default: m.EquipmentManagement })));
const BrowseEquipment = lazy(() => import('./components/BrowseEquipment').then(m => ({ default: m.BrowseEquipment })));
const BorrowRequests = lazy(() => import('./components/BorrowRequests').then(m => ({ default: m.BorrowRequests })));
const MyRequests = lazy(() => import('./components/MyRequests').then(m => ({ default: m.MyRequests })));
const UserManagement = lazy(() => import('./components/UserManagement').then(m => ({ default: m.UserManagement })));
const DocumentManagement = lazy(() => import('./components/DocumentManagement').then(m => ({ default: m.DocumentManagement })));
const StudentForms = lazy(() => import('./components/StudentForms').then(m => ({ default: m.StudentForms })));
const TransactionHistory = lazy(() => import('./components/TransactionHistory').then(m => ({ default: m.TransactionHistory })));
const PlayerEvaluation = lazy(() => import('./components/PlayerEvaluation').then(m => ({ default: m.PlayerEvaluation })));
const CoachDashboard = lazy(() => import('./components/CoachDashboard').then(m => ({ default: m.CoachDashboard })));
const FacultyDashboard = lazy(() => import('./components/FacultyDashboard').then(m => ({ default: m.FacultyDashboard })));
const SportsManagement = lazy(() => import('./components/SportsManagement').then(m => ({ default: m.SportsManagement })));
const SportsRegistration = lazy(() => import('./components/SportsRegistration').then(m => ({ default: m.SportsRegistration })));
const MyHistory = lazy(() => import('./components/MyHistory').then(m => ({ default: m.MyHistory })));
const Settings = lazy(() => import('./components/Settings').then(m => ({ default: m.Settings })));
const ProfileManagement = lazy(() => import('./components/ProfileManagement').then(m => ({ default: m.ProfileManagement })));
const FeedbackView = lazy(() => import('./components/FeedbackView').then(m => ({ default: m.FeedbackView })));
const FeedbackForm = lazy(() => import('./components/FeedbackForm').then(m => ({ default: m.FeedbackForm })));
const PromotionalVideo = lazy(() => import('./components/PromotionalVideo').then(m => ({ default: m.PromotionalVideo })));
const ChatBot = lazy(() => import('./components/ChatBot').then(m => ({ default: m.ChatBot })));

function LazyFallback() {
  return (
    <div className="flex items-center justify-center p-8">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
}

export default function App() {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [currentView, setCurrentView] = useState('dashboard');
  const [appView, setAppView] = useState<'landing' | 'login' | 'signup' | 'app'>('landing');
  const [users, setUsers] = useState<User[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [requests, setRequests] = useState<BorrowRequest[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [equipmentToOpen, setEquipmentToOpen] = useState<string | undefined>(undefined);
  const [pendingEquipmentFromLanding, setPendingEquipmentFromLanding] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [showPromoVideo, setShowPromoVideo] = useState(false);
  const [hasSeenVideo, setHasSeenVideo] = useState(false);
  const [feedbackRequest, setFeedbackRequest] = useState<BorrowRequest | null>(null);
  const [loadedData, setLoadedData] = useState({
    equipment: false,
    requests: false,
    documents: false,
    users: false,
  });

  // Check for existing session on mount
  useEffect(() => {
    checkSession();
  }, []);

  // Set up auth state listener for automatic token refresh
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'TOKEN_REFRESHED') {
        if (session?.access_token) {
          api.setAccessToken(session.access_token);
        }
      } else if (event === 'SIGNED_OUT') {
        api.clearAccessToken();
        if (currentUser) {
          handleLogout();
        }
      } else if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
        if (session?.access_token) {
          api.setAccessToken(session.access_token);
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [currentUser]);

  // Load data when user first logs in
  useEffect(() => {
    if (currentUser) {
      void loadInitialData(currentUser);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    void ensureCurrentViewData(currentUser, currentView);
  }, [currentUser, currentView]);

  // Periodic token refresh - refresh every 30 minutes to prevent expiration
  useEffect(() => {
    if (!currentUser) return;

    const refreshInterval = setInterval(async () => {
      try {
        const { data: sessionData } = await getSession();
        if (sessionData?.session?.access_token) {
          api.setAccessToken(sessionData.session.access_token);
        }
      } catch (error) {
        console.error('Periodic token refresh error:', error);
      }
    }, 30 * 60 * 1000); // 30 minutes

    return () => {
      clearInterval(refreshInterval);
    };
  }, [currentUser]);

  const checkSession = async () => {
    try {
      const { data, error } = await getSession();
      
      if (error || !data?.session) {
        api.clearAccessToken();
        setIsLoading(false);
        return;
      }

      if (data.session?.access_token) {
        api.setAccessToken(data.session.access_token);
      } else {
        api.clearAccessToken();
        setIsLoading(false);
        return;
      }

      if (isPendingCoachAuthUser(data.session.user)) {
        api.clearAccessToken();
        await signOut().catch(() => {});
        setAppView('login');
        return;
      }

      try {
        const { user } = await api.getCurrentUser();
        setCurrentUser(user);
        setAppView('app');
      } catch (userError: any) {
        api.clearAccessToken();
        await signOut().catch(() => {});
      }
    } catch (error: any) {
      api.clearAccessToken();
    } finally {
      setIsLoading(false);
    }
  };

  const resetLoadedData = () => {
    setLoadedData({
      equipment: false,
      requests: false,
      documents: false,
      users: false,
    });
  };

  const loadEquipment = async () => {
    const { equipment: nextEquipment } = await api.getEquipment();
    setEquipment(nextEquipment);
    setLoadedData((prev) => ({ ...prev, equipment: true }));
  };

  const loadRequests = async () => {
    const { requests: nextRequests } = await api.getRequests();
    setRequests(nextRequests);
    setLoadedData((prev) => ({ ...prev, requests: true }));
  };

  const loadDocuments = async () => {
    const { documents: nextDocuments } = await api.getDocuments();
    setDocuments(nextDocuments);
    setLoadedData((prev) => ({ ...prev, documents: true }));
  };

  const loadUsers = async () => {
    const { users: nextUsers } = await api.getUsers();
    setUsers(nextUsers);
    setLoadedData((prev) => ({ ...prev, users: true }));
  };

  const ensureCurrentViewData = async (user: User, view: string) => {
    try {
      if (view === 'browse' && !loadedData.equipment) {
        await loadEquipment();
      }

      if ((view === 'my-requests' || view === 'history') && !loadedData.requests) {
        await loadRequests();
      }

      if ((view === 'documents' || view === 'forms') && !loadedData.documents) {
        await loadDocuments();
      }

      if ((view === 'users' || view === 'student-data-bank') && user.role === 'admin' && !loadedData.users) {
        await loadUsers();
      }
    } catch (error: any) {
      console.error('Error loading view data:', error);
      toast.error(`Failed to load data: ${error.message || 'Unknown error'}`);
    }
  };

  const loadInitialData = async (user: User) => {
    try {
      const currentToken = api.getAccessToken();
      if (!currentToken) {
        const { data: sessionData, error: sessionError } = await getSession();
        
        if (sessionError || !sessionData?.session?.access_token) {
          api.clearAccessToken();
          setCurrentUser(null);
          toast.error('Session expired. Please login again.');
          return;
        }
        
        api.setAccessToken(sessionData.session.access_token);
      }

      const fetches: Promise<void>[] = [];

      if (user.role !== 'coach') {
        if (!loadedData.equipment) {
          fetches.push(loadEquipment());
        }

        if (!loadedData.requests) {
          fetches.push(loadRequests());
        }
      } else if (pendingEquipmentFromLanding) {
        if (!loadedData.equipment) {
          fetches.push(loadEquipment());
        }

        if (!loadedData.requests) {
          fetches.push(loadRequests());
        }
      }

      await Promise.all(fetches);
    } catch (error: any) {
      // Check if it's an auth error
      if (error.message?.includes('Unauthorized') || error.message?.includes('Invalid token')) {
        api.clearAccessToken();
        setCurrentUser(null);
        resetLoadedData();
      } else {
        console.error('Error loading data:', error);
        toast.error(`Failed to load data: ${error.message || 'Unknown error'}`);
      }
    }
  };

  const handleLogin = async (user: User) => {
    const token = api.getAccessToken();
    if (!token) {
      toast.error('Login failed: No authentication token. Please try again.');
      return;
    }
    
    setCurrentUser(user);
    setAppView('app');
    resetLoadedData();
    
    // Set default view based on role
    if (user.role === 'admin') {
      setCurrentView('dashboard');
    } else if (user.role === 'coach') {
      if (pendingEquipmentFromLanding) {
        setCurrentView('browse');
        setEquipmentToOpen(pendingEquipmentFromLanding);
        setPendingEquipmentFromLanding(undefined);
      } else {
        setCurrentView('dashboard');
      }
    } else {
      // Students and faculty/staff default to browse equipment
      setCurrentView('browse');
      
      // If there's a pending equipment from the landing page, auto-open borrow dialog
      if (pendingEquipmentFromLanding) {
        setEquipmentToOpen(pendingEquipmentFromLanding);
        setPendingEquipmentFromLanding(undefined);
      }
    }
  };

  const handleLogout = async () => {
    try {
      // Always clear local state and token first
      api.clearAccessToken();
      setCurrentUser(null);
      setCurrentView('dashboard');
      setAppView('landing');
      setUsers([]);
      setEquipment([]);
      setRequests([]);
      setDocuments([]);
      resetLoadedData();
      setHasSeenVideo(true); // Don't show video again after logout
      
      // Try to sign out from Supabase (will handle gracefully if no session)
      await signOut();
      
      // Only show success toast if this was a user-initiated logout
      // (not an automatic logout due to expired token)
      if (currentUser) {
        toast.success('Logged out successfully');
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Don't show error toast - state is already cleared
    }
  };

  const handleAddEquipment = async (newEquipment: Omit<Equipment, 'id' | 'available'>) => {
    try {
      const { equipment: createdEquipment } = await api.createEquipment(newEquipment);
      setEquipment((prev) => [...prev, createdEquipment]);
      setLoadedData((prev) => ({ ...prev, equipment: true }));
      toast.success('Equipment added successfully!');
    } catch (error) {
      console.error('Error adding equipment:', error);
      toast.error('Failed to add equipment');
    }
  };

  const handleUpdateEquipment = async (id: string, updates: Partial<Equipment>) => {
    try {
      const { equipment: updatedEquipment } = await api.updateEquipment(id, updates);
      setEquipment((prev) =>
        prev.map((item) => (item.id === id ? updatedEquipment : item))
      );
      toast.success('Equipment updated successfully!');
    } catch (error) {
      console.error('Error updating equipment:', error);
      toast.error('Failed to update equipment');
    }
  };

  const handleDeleteEquipment = async (id: string) => {
    try {
      console.log('Attempting to delete equipment with ID:', id);
      const equipmentItem = equipment.find(e => e.id === id);
      console.log('Equipment item found in state:', equipmentItem);
      
      await api.deleteEquipment(id);
      setEquipment((prev) => prev.filter((item) => item.id !== id));
      toast.success('Equipment deleted successfully!');
    } catch (error: any) {
      console.error('Error deleting equipment:', error);
      console.error('Error message:', error.message);
      toast.error(`Failed to delete equipment: ${error.message || 'Unknown error'}`);
    }
  };

  const createManagedUser = async (newUser: Omit<User, 'id'> & { password?: string }) => {
    if (!newUser.email || !newUser.name || !newUser.role) {
      throw new Error('Please fill in all required fields');
    }

    const nameValidation = validateHumanName(newUser.name);
    if (!nameValidation.isValid) {
      throw new Error(nameValidation.error || 'Please enter a valid name');
    }

    const password = (newUser as { password?: string }).password;
    if (!password) {
      throw new Error('Password is required');
    }

    const studentId = newUser.idNumber || newUser.studentId || undefined;

    const { user: createdUser } = await api.signup({
      email: newUser.email.trim().toLowerCase(),
      password,
      name: nameValidation.normalizedName,
      role: newUser.role,
      department: newUser.department || undefined,
      studentId,
      assignedSport: newUser.role === 'coach' ? (newUser as { assignedSport?: string }).assignedSport || undefined : undefined,
      skipStudentDataBankValidation: true,
    });

    return createdUser;
  };

  const handleAddUser = async (newUser: Omit<User, 'id'>) => {
    try {
      const createdUser = await createManagedUser(newUser as Omit<User, 'id'> & { password?: string });
      setUsers((prev) => [...prev, createdUser]);
      setLoadedData((prev) => ({ ...prev, users: true }));
      toast.success('User created successfully!');
    } catch (error: any) {
      console.error('Error adding user:', error);
      toast.error(error.message || 'Failed to create user');
    }
  };

  const handleImportStudents = async (
    studentsToImport: Array<Omit<User, 'id'> & { password: string; sourceRowNumber?: number }>
  ) => {
    const createdUsers: User[] = [];
    const failed: Array<{ rowNumber: number; email: string; reason: string }> = [];

    for (const student of studentsToImport) {
      try {
        const createdUser = await createManagedUser(student);
        createdUsers.push(createdUser);
      } catch (error: any) {
        failed.push({
          rowNumber: student.sourceRowNumber || 0,
          email: student.email,
          reason: error?.message || 'Failed to create student account',
        });
      }
    }

    if (createdUsers.length > 0) {
      setUsers((prev) => [...createdUsers, ...prev]);
      setLoadedData((prev) => ({ ...prev, users: true }));
    }

    return {
      created: createdUsers.length,
      failed,
    };
  };

  const handleUpdateUser = async (id: string, updates: Partial<User>) => {
    try {
      const nextUpdates = { ...updates };
      if (typeof nextUpdates.name === 'string') {
        const nameValidation = validateHumanName(nextUpdates.name);
        if (!nameValidation.isValid) {
          toast.error(nameValidation.error || 'Please enter a valid name');
          return;
        }
        nextUpdates.name = nameValidation.normalizedName;
      }

      const { user: updatedUser } = await api.updateUser(id, nextUpdates);
      setUsers((prev) =>
        prev.map((user) => (user.id === id ? updatedUser : user))
      );
      toast.success('User updated successfully!');
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    }
  };

  const handleVerifyCoach = async (id: string) => {
    try {
      const { user: verifiedCoach, emailSent, emailError } = await api.verifyCoach(id);
      setUsers((prev) => prev.map((user) => (user.id === id ? verifiedCoach : user)));

      if (emailSent) {
        toast.success('Coach verified and approval email sent.');
      } else {
        toast.success(emailError
          ? `Coach verified. Approval email was not sent automatically: ${emailError}`
          : 'Coach verified. Approval email was not sent automatically.');
      }
    } catch (error: any) {
      console.error('Error verifying coach:', error);
      toast.error(error.message || 'Failed to verify coach');
    }
  };

  const handleDeleteUser = async (id: string) => {
    try {
      await api.deleteUser(id);
      setUsers((prev) => prev.filter((user) => user.id !== id));
      toast.success('User deleted successfully!');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const handleBorrow = async (
    equipmentId: string,
    quantity: number,
    purpose: string
  ) => {
    try {
      const equipmentItem = equipment.find((e) => e.id === equipmentId);
      if (!equipmentItem || !currentUser) return;

      const borrowDate = new Date().toISOString().split('T')[0];

      const { request: newRequest } = await api.createRequest({
        equipmentId,
        quantity,
        borrowDate,
        purpose
      });

      setRequests((prev) => [newRequest, ...prev]);
      
      // IMMEDIATELY deduct availability (equipment is now reserved)
      setEquipment((prev) =>
        prev.map((item) =>
          item.id === equipmentId
            ? { ...item, available: item.available - quantity }
            : item
        )
      );
      
      toast.success(`Request submitted! ${equipmentItem.name} reserved (${quantity} unit${quantity > 1 ? 's' : ''}).`);
    } catch (error: any) {
      console.error('Error creating borrow request:', error);
      toast.error(error.message || 'Failed to submit borrow request');
    }
  };

  const handleNavigateFromAI = (view: string) => {
    setCurrentView(view);
  };

  const handleBorrowFromAI = (equipmentName: string) => {
    setEquipmentToOpen(equipmentName);
    setCurrentView('browse');
  };

  const handleApproveRequest = async (id: string, dueDate: string) => {
    try {
      const { request: updatedRequest } = await api.updateRequest(id, { status: 'approved', dueDate });
      setRequests((prev) =>
        prev.map((req) => (req.id === id ? updatedRequest : req))
      );
      
      // No need to deduct availability - already deducted when request was created (pending)
      toast.success('Request approved with due date set successfully!');
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error('Failed to approve request');
    }
  };

  const handleRejectRequest = async (id: string) => {
    try {
      const request = requests.find(r => r.id === id);
      
      await api.updateRequest(id, { status: 'denied' });
      setRequests((prev) => prev.filter((req) => req.id !== id));
      
      // Restore availability since equipment was reserved when request was pending
      if (request) {
        setEquipment((prev) =>
          prev.map((item) =>
            item.id === request.equipmentId
              ? { ...item, available: item.available + request.quantity }
              : item
          )
        );
        toast.success(`Request denied. ${request.equipmentName} availability restored.`);
      } else {
        toast.success('Request rejected');
      }
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error('Failed to reject request');
    }
  };

  const handleMarkBorrowed = async (id: string) => {
    try {
      const { request: updatedRequest } = await api.updateRequest(id, { status: 'borrowed' });
      setRequests((prev) =>
        prev.map((req) => (req.id === id ? updatedRequest : req))
      );
      toast.success('Equipment marked as borrowed');
    } catch (error) {
      console.error('Error marking as borrowed:', error);
      toast.error('Failed to mark as borrowed');
    }
  };

  const handleMarkReturned = async (id: string) => {
    try {
      const returnDate = new Date().toISOString().split('T')[0];
      const { request: updatedRequest } = await api.updateRequest(id, { 
        status: 'returned',
        returnDate 
      });
      
      setRequests((prev) =>
        prev.map((req) => (req.id === id ? updatedRequest : req))
      );

      // Update equipment availability
      const request = requests.find(r => r.id === id);
      if (request) {
        setEquipment((prev) =>
          prev.map((item) =>
            item.id === request.equipmentId
              ? { ...item, available: item.available + request.quantity }
              : item
          )
        );
        
        toast.success(`Equipment returned! ${request.equipmentName} availability increased by ${request.quantity}.`);
        
        // Show feedback form for non-admin users
        if (request.userId === currentUser?.id && currentUser.role !== 'admin') {
          setFeedbackRequest(updatedRequest);
        }
      } else {
        toast.success('Equipment returned successfully');
      }
    } catch (error) {
      console.error('Error marking as returned:', error);
      toast.error('Failed to mark as returned');
    }
  };

  const handleUpdateCurrentUser = (updatedUser: User) => {
    setCurrentUser(updatedUser);
  };

  const handleUploadDocument = async (file: File, category: string) => {
    try {
      const { document } = await api.uploadDocument(file, category);
      setDocuments((prev) => [document, ...prev]);
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  };

  const handleDeleteDocument = async (id: string) => {
    try {
      await api.deleteDocument(id);
      setDocuments((prev) => prev.filter((doc) => doc.id !== id));
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  };

  const handleDownloadDocument = async (doc: Document) => {
    try {
      const { url } = await api.getDocumentDownloadUrl(doc.id);
      // Open download in new tab
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error downloading document:', error);
      throw error;
    }
  };

  const handleVideoComplete = () => {
    setShowPromoVideo(false);
    setHasSeenVideo(true);
  };

  const handleVideoSkip = () => {
    setShowPromoVideo(false);
    setHasSeenVideo(true);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading SPORTRIX AI...</p>
        </div>
      </div>
    );
  }

  // Show promotional video before landing page if not seen yet
  if (!currentUser && !hasSeenVideo && appView === 'landing') {
    return (
      <Suspense fallback={<LazyFallback />}>
        <PromotionalVideo
          isOpen={true}
          onComplete={handleVideoComplete}
          onSkip={handleVideoSkip}
        />
      </Suspense>
    );
  }

  // Show landing page (public)
  if (!currentUser && appView === 'landing') {
    return (
      <LandingPage
        onNavigateToLogin={() => setAppView('login')}
        onNavigateToSignup={() => setAppView('signup')}
        onNavigateToLoginWithEquipment={(equipmentName) => {
          setPendingEquipmentFromLanding(equipmentName);
          setAppView('login');
        }}
        onNavigateToSignupWithEquipment={(equipmentName) => {
          setPendingEquipmentFromLanding(equipmentName);
          setAppView('signup');
        }}
      />
    );
  }

  // Show login/signup page
  if (!currentUser) {
    return (
      <div className="relative">
        {/* Back to landing button */}
        <button
          onClick={() => setAppView('landing')}
          className="fixed top-3 left-3 sm:top-4 sm:left-4 z-50 flex items-center gap-1.5 sm:gap-2 px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm text-slate-400 hover:text-white bg-slate-900/70 hover:bg-slate-800/80 backdrop-blur-md border border-white/10 hover:border-white/20 rounded-xl shadow-lg transition-all duration-200"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sm:w-4 sm:h-4"><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>
          <span className="hidden xs:inline sm:inline">Back to Home</span>
          <span className="xs:hidden sm:hidden">Home</span>
        </button>
        <LoginPage
          onLogin={handleLogin}
          initialTab={appView === 'signup' ? 'signup' : 'login'}
          pendingEquipmentName={pendingEquipmentFromLanding}
        />
      </div>
    );
  }

  const renderView = () => {
    if (currentView === 'profile') {
      return <ProfileManagement user={currentUser} onUpdate={handleUpdateCurrentUser} />;
    }

    if (currentUser.role === 'admin') {
      switch (currentView) {
        case 'dashboard':
          return <AdminDashboard equipment={equipment} requests={requests} />;
        case 'student-data-bank':
          return (
            <StudentDataBank
              users={users}
              onAddStudent={handleAddUser}
              onImportStudents={handleImportStudents}
              onUpdateStudent={handleUpdateUser}
              onDeleteStudent={handleDeleteUser}
            />
          );
        case 'equipment':
          return (
            <EquipmentManagement
              equipment={equipment}
              onAdd={handleAddEquipment}
              onUpdate={handleUpdateEquipment}
              onDelete={handleDeleteEquipment}
            />
          );
        case 'requests':
          return (
            <BorrowRequests
              requests={requests}
              onApprove={handleApproveRequest}
              onReject={handleRejectRequest}
              onMarkBorrowed={handleMarkBorrowed}
              onMarkReturned={handleMarkReturned}
            />
          );
        case 'users':
          return (
            <UserManagement
              users={users}
              onAddUser={handleAddUser}
              onUpdateUser={handleUpdateUser}
              onDeleteUser={handleDeleteUser}
              onVerifyCoach={handleVerifyCoach}
            />
          );
        case 'documents':
          return (
            <DocumentManagement
              documents={documents}
              onUpload={handleUploadDocument}
              onDelete={handleDeleteDocument}
              onDownload={handleDownloadDocument}
            />
          );
        case 'feedback':
          return <FeedbackView />;
        case 'sports-management':
          return <SportsManagement />;
        case 'history':
          return <TransactionHistory requests={requests} />;
        case 'settings':
          return <Settings />;
        default:
          return <AdminDashboard equipment={equipment} requests={requests} />;
      }
    } else if (currentUser.role === 'coach') {
      switch (currentView) {
        case 'dashboard':
          return <CoachDashboard user={currentUser} />;
        case 'sports-evaluation':
          return <PlayerEvaluation user={currentUser} />;
        case 'browse':
          return (
            <BrowseEquipment
              equipment={equipment}
              user={currentUser}
              onBorrow={handleBorrow}
              equipmentToOpen={equipmentToOpen}
              onEquipmentToOpenConsumed={() => setEquipmentToOpen(undefined)}
            />
          );
        case 'my-requests':
          return (
            <MyRequests
              requests={requests}
              user={currentUser}
              onProvideFeedback={(request) => setFeedbackRequest(request)}
            />
          );
        case 'forms':
          return (
            <StudentForms
              documents={documents}
              onDownload={handleDownloadDocument}
            />
          );
        case 'history':
          return (
            <MyHistory
              requests={requests}
              user={currentUser}
              onProvideFeedback={(request) => setFeedbackRequest(request)}
            />
          );
        default:
          return <CoachDashboard user={currentUser} />;
      }
    } else if (currentUser.role === 'faculty') {
      switch (currentView) {
        case 'dashboard':
          return <FacultyDashboard user={currentUser} requests={requests} onNavigate={setCurrentView} />;
        case 'browse':
          return (
            <BrowseEquipment
              equipment={equipment}
              user={currentUser}
              onBorrow={handleBorrow}
              equipmentToOpen={equipmentToOpen}
              onEquipmentToOpenConsumed={() => setEquipmentToOpen(undefined)}
            />
          );
        case 'my-requests':
          return (
            <MyRequests 
              requests={requests} 
              user={currentUser}
              onProvideFeedback={(request) => setFeedbackRequest(request)}
            />
          );
        case 'forms':
          return (
            <StudentForms
              documents={documents}
              onDownload={handleDownloadDocument}
            />
          );
        case 'history':
          return (
            <MyHistory 
              requests={requests} 
              user={currentUser}
              onProvideFeedback={(request) => setFeedbackRequest(request)}
            />
          );
        default:
          return <FacultyDashboard user={currentUser} requests={requests} onNavigate={setCurrentView} />;
      }
    } else {
      switch (currentView) {
        case 'dashboard':
          return <UserDashboard user={currentUser} requests={requests} onNavigate={setCurrentView} />;
        case 'browse':
          return (
            <BrowseEquipment
              equipment={equipment}
              user={currentUser}
              onBorrow={handleBorrow}
              equipmentToOpen={equipmentToOpen}
              onEquipmentToOpenConsumed={() => setEquipmentToOpen(undefined)}
            />
          );
        case 'my-requests':
          return (
            <MyRequests 
              requests={requests} 
              user={currentUser}
              onProvideFeedback={(request) => setFeedbackRequest(request)}
            />
          );
        case 'forms':
          return (
            <StudentForms
              documents={documents}
              onDownload={handleDownloadDocument}
            />
          );
        case 'sports-registration':
          if (currentUser.role === 'student') {
            return <SportsRegistration user={currentUser} />;
          }
          return <UserDashboard user={currentUser} requests={requests} onNavigate={setCurrentView} />;
        case 'history':
          return (
            <MyHistory 
              requests={requests} 
              user={currentUser}
              onProvideFeedback={(request) => setFeedbackRequest(request)}
            />
          );
        default:
          return <UserDashboard user={currentUser} requests={requests} />;
      }
    }
  };

  return (
    <>
      <Toaster position="top-right" />
      <DashboardLayout
        user={currentUser}
        onLogout={handleLogout}
        onNavigate={setCurrentView}
        navigation={
          <Navigation
            user={currentUser}
            currentView={currentView}
            onNavigate={setCurrentView}
          />
        }
      >
        <Suspense fallback={<LazyFallback />}>
          {renderView()}
        </Suspense>
      </DashboardLayout>

      {/* ChatBot - Only for non-admin and non-coach users */}
      {currentUser.role !== 'admin' && currentUser.role !== 'coach' && (
        <Suspense fallback={null}>
          <ChatBot />
        </Suspense>
      )}

      {/* Feedback Form - Show after equipment return */}
      {feedbackRequest && (
        <Suspense fallback={null}>
          <FeedbackForm
            isOpen={true}
            onClose={() => setFeedbackRequest(null)}
            request={feedbackRequest}
            onFeedbackSubmitted={() => {
              if (currentUser) {
                void loadRequests();
              }
            }}
          />
        </Suspense>
      )}
    </>
  );
}